import React, { useRef, useState } from 'react'
import { Container, TitleContainer, Title, Heading, SpinnerContainer } from './PaymentMethod.style'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import useGetCheckoutSession from '@hooks/useGetCheckoutSession'
import { useTranslation } from 'react-i18next'
import Spinner from 'parkdepot-shared/components/Spinner'
import createAdyenDropin from '@utils/createAdyenDropin.utils'
import DropinElement from '@adyen/adyen-web/dist/types/components/Dropin/Dropin'
import DropIn from '@components/Dropin/Dropin'
import { OnPaymentCompletedData } from '@adyen/adyen-web/dist/types/components/types'
import { captureException } from '@sentry/react'
import useCarpayPaymentFlow from '@hooks/useCarpayPaymentFlow'
import Error from '@components/Error/Error.component'
import { useApolloClient } from '@apollo/client'
import { OrderResponseFragment } from '@graphql/queries/getOpenOrders.query'
import { CARPAY_STATUS } from 'parkdepot-shared/models/database'
import { savePaidOrder } from '@utils/carpaySavedPaidOrders.utils'
import useLanguage from '@hooks/useLanguage.hook'
import { useAuth } from '@context/AuthContext/AuthContext'
import { getFragmentData } from 'parkdepot-shared/gql'
import useCarpayOrder from '@hooks/useCarpayOrder.hook'
import useGetErrorFlowCheckoutSession from '@hooks/useGetErrorFlowCheckoutSession'
import { CarpayOrderActionsStep } from '@components/CarpayOrderActions/CarpayOrderActions.component'

const CarpayPaymentMethod = () => {
    const [lng] = useLanguage()
    const history = useHistory()
    const client = useApolloClient()
    const { isLoggedIn } = useAuth()
    const { t } = useTranslation()
    const [dropin, setDropin] = useState<null | DropinElement>(null)
    const [adyenError, setAdyenError] = useState<string | null>(null)
    const { order_id } = useParams<{ order_id: string }>()
    const { state } = useCarpayPaymentFlow()
    const dropInRef = useRef<null | HTMLDivElement>(null)
    const { search } = useLocation()
    const queryparams = new URLSearchParams(search)
    const plate = queryparams.get('plate')
    const country = queryparams.get('country')

    const { data, loading, error, errorFlowData } = useCarpayOrder(plate)

    const onSessionComplete = (adyenId: string, adyenSessionData: string) => {
        const onCompleted = (result: OnPaymentCompletedData) => {
            const isErrorFlow = order_id === CarpayOrderActionsStep.ErrorFlow
            // changed from ['Cancelled', 'Error', 'Refused'] to only Error
            if (['Error'].indexOf(result.resultCode) >= 0) {
                captureException(`Payment for order ${order_id} failed`, {
                    extra: { order_id, paymentCode: result.resultCode }
                })
            }

            // If something goes wrong, we always redirect to the payment finished page despite the user being logged in or not.
            if (['Cancelled', 'Error', 'Refused'].indexOf(result.resultCode) >= 0) {
                return history.replace(`/carpay/orders/${order_id}/payment-finished?status=${result.resultCode}`)
            }

            if (!isErrorFlow) {
                client.cache.updateFragment({ id: order_id, fragment: OrderResponseFragment }, (data) => {
                    if (!data) return

                    return {
                        ...data,
                        order: {
                            ...data.order,
                            status: CARPAY_STATUS.PAID
                        }
                    }
                })
            }

            if (isLoggedIn && !isErrorFlow) {
                const paidOrder = data!.b2c_get_open_orders.orders.map((data) =>
                    getFragmentData(OrderResponseFragment, data)
                )[0]
                savePaidOrder(order_id)
                history.replace(
                    `/carpay/paid-order?plate=${paidOrder.order.plate}&order_id=${order_id}&country=${country}&status=${result.resultCode}`
                )
            } else {
                history.replace(
                    `/carpay/orders/${order_id}/payment-finished?status=${result.resultCode}&plate=${plate}&country=${country}`
                )
            }
        }

        const initAdyenDropIn = async (adyenId: string, adyenSessionData: string) => {
            // TODO: Handle error
            const adyenInfo = await createAdyenDropin(
                {
                    id: adyenId,
                    sessionData: adyenSessionData
                },
                onCompleted,
                () => console.log('error')
            )

            if (typeof adyenInfo === 'string') setAdyenError(adyenInfo)
            else setDropin(adyenInfo.dropin)
        }

        initAdyenDropIn(adyenId, adyenSessionData)
    }

    useGetCheckoutSession({
        skip: order_id === CarpayOrderActionsStep.ErrorFlow || !data?.b2c_get_open_orders,
        fetchPolicy: 'no-cache',
        variables: {
            order_id: order_id,
            language: lng.replace('-', '_'),
            email: state.email,
            parking_context_uuid: state.parkingContextUuid ?? undefined,
            selected_parking_tariff: state.parkingTariff ?? undefined
        },
        onCompleted: (checkoutData) => {
            const { adyen_id: adyenId, adyen_session_data: adyenSessionData } = checkoutData.b2c_checkout
            onSessionComplete(adyenId, adyenSessionData)
        }
    })

    useGetErrorFlowCheckoutSession({
        skip: order_id !== CarpayOrderActionsStep.ErrorFlow,
        fetchPolicy: 'no-cache',
        variables: {
            lot_id: errorFlowData?.lot?.id ?? '',
            plate: errorFlowData?.plate ?? '',
            country: errorFlowData?.country?.toUpperCase() ?? '',
            start_date: errorFlowData?.start_datetime ?? '',
            end_date: errorFlowData?.end_datetime ?? '',
            email: state.email
        },
        onCompleted: (checkoutData) => {
            const { adyen_id: adyenId, adyen_session_data: adyenSessionData } = checkoutData.b2c_errorflow_checkout
            onSessionComplete(adyenId, adyenSessionData)
        }
    })

    if (loading) {
        return (
            <SpinnerContainer>
                <Spinner theme="primary" size="xl" />
            </SpinnerContainer>
        )
    }

    if (error) {
        return <Error />
    }

    if (!data?.b2c_get_open_orders && !errorFlowData) return <div>{t('carpay.notFound.orderNotFound')}</div>

    return (
        <Container data-testid="payment-method-container">
            <TitleContainer>
                <Heading>{t('carpay.paymentMethod.title')}</Heading>
                <Title>{t('carpay.paymentMethod.subtitle')}</Title>
            </TitleContainer>

            <DropIn dropInRef={dropInRef} dropin={dropin} error={adyenError} />
        </Container>
    )
}

export default CarpayPaymentMethod
